import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock3, CloudCog, FileText, Inbox, Pencil, RadioTower, RefreshCw, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { EmptyState, Modal, PageHeader, StatCard, StatusBadge, Surface } from '../components/ui';
import DeviceLogo, { DeviceHardwareLabel } from '../components/DeviceLogo';

const ONLINE_MS = 2 * 60 * 1000;
const asUtcDate = (value) => value ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`) : null;
const isOnline = (lastSeen) => lastSeen && (Date.now() - asUtcDate(lastSeen).getTime()) < ONLINE_MS;

function formatLastSeen(lastSeen, t) {
  if (!lastSeen) return null;
  const date = asUtcDate(lastSeen);
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return t('userDashboard.justNow');
  if (diffMin < 60) return t('userDashboard.minsAgo', { n: diffMin });
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return t('userDashboard.hoursAgo', { n: diffHours });
  return t('userDashboard.daysAgo', { date: date.toLocaleDateString() });
}

function DeviceCard({ device, onRename, onClick, t }) {
  const online = device.status === 'provisioned' && isOnline(device.last_seen);
  const pending = device.status !== 'provisioned';
  const tone = pending ? 'warning' : online ? 'success' : 'muted';
  const status = pending ? t('userDashboard.statusPending') : online ? t('userDashboard.statusOnline') : t('userDashboard.statusOffline');

  return (
    <article className={`surface-card device-card tone-${tone}`} role="button" tabIndex={0} onClick={onClick} onKeyDown={(event) => (event.key === 'Enter' || event.key === ' ') && onClick()}>
      <div className="device-card-heading">
        <DeviceLogo device={device} />
        <div className="min-w-0 flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <span className="device-title">{device.name || t('userDashboard.unnamedDevice')}</span>
            <button type="button" className="icon-btn" onClick={(event) => { event.stopPropagation(); onRename(device); }} title={t('userDashboard.renameDevice')} aria-label={t('userDashboard.renameDevice')}>
              <Pencil className="icon" aria-hidden="true" />
            </button>
          </div>
          <DeviceHardwareLabel device={device} />
          <code className="small text-muted d-block mt-1 text-break">{device.serial_number}</code>
        </div>
      </div>
      <div className="mt-2">
        <StatusBadge tone={tone}>{status}</StatusBadge>
      </div>
      <div className="device-meta">
        {device.transport && <span className="inline-icon"><ShieldCheck className="icon" aria-hidden="true" /><code>{device.transport}</code></span>}
        {device.last_seen && <span className="inline-icon"><Clock3 className="icon" aria-hidden="true" />{formatLastSeen(device.last_seen, t)}</span>}
        {device.notes && <span className="inline-icon"><FileText className="icon" aria-hidden="true" /><span className="text-truncate">{device.notes}</span></span>}
      </div>
    </article>
  );
}

function ProvisionBlock({ t }) {
  return (
    <Surface className="mt-4" title={t('userDashboard.provisionTitle')} icon={CloudCog}>
      <div className="surface-body">
        <p className="mb-2">{t('userDashboard.provisionDesc')}</p>
        <p className="text-muted small mb-0">{t('userDashboard.provisionAccountHint')}</p>
      </div>
    </Surface>
  );
}

export default function UserDashboardPage() {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [renameDevice, setRenameDevice] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoadError('');
    api.getDevices().then((data) => setDevices(data)).catch((err) => setLoadError(err.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const openRename = (device) => { setRenameDevice(device); setRenameName(device.name || ''); setRenameError(''); };
  const handleRename = async (event) => {
    event.preventDefault(); setRenameSaving(true); setRenameError('');
    try { await api.updateDevice(renameDevice.id, { name: renameName }); setRenameDevice(null); load(); }
    catch (err) { setRenameError(err.message); }
    finally { setRenameSaving(false); }
  };

  const online = devices.filter((device) => device.status === 'provisioned' && isOnline(device.last_seen)).length;
  const pending = devices.filter((device) => device.status !== 'provisioned').length;

  return (
    <div className="page">
      <PageHeader icon={RadioTower} title={t('userDashboard.title')} subtitle={t('userDashboard.subtitle')} actions={
        <button type="button" className="btn btn-outline-secondary" onClick={load}><RefreshCw className="icon" aria-hidden="true" />{t('userDashboard.refresh')}</button>
      } />
      {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}

      {!loading && devices.length > 0 && <div className="stat-grid">
        <StatCard icon={RadioTower} label={t('userDashboard.statsTotal')} value={devices.length} />
        <StatCard icon={ShieldCheck} label={t('userDashboard.statsOnline')} value={online} tone="success" />
        <StatCard icon={Clock3} label={t('userDashboard.statsPending')} value={pending} tone="warning" />
      </div>}

      {loading ? <div className="empty-state"><div className="spinner-border" role="status" aria-label={t('adminSettings.loading')} /></div>
        : devices.length === 0 ? <Surface><EmptyState icon={Inbox} title={t('userDashboard.emptyTitle')} hint={t('userDashboard.emptyHint')} /></Surface>
          : <div className="device-grid">{devices.map((device) => <DeviceCard key={device.id} device={device} t={t} onRename={openRename} onClick={() => navigate(`/devices/${device.id}`)} />)}</div>}

      <ProvisionBlock t={t} />

      {renameDevice && <Modal title={t('userDashboard.renameTitle')} onClose={() => setRenameDevice(null)} labelledBy="rename-device-title">
        <p className="small text-muted font-monospace text-break">{renameDevice.serial_number}</p>
        {renameError && <div className="alert alert-danger py-2 small" role="alert">{renameError}</div>}
        <form onSubmit={handleRename}>
          <label className="form-label" htmlFor="rename-device-name">{t('userDashboard.renameTitle')}</label>
          <input id="rename-device-name" className="form-control" value={renameName} onChange={(event) => setRenameName(event.target.value)} placeholder={t('userDashboard.renamePlaceholder')} autoFocus />
          <div className="modal-actions"><button type="button" className="btn btn-outline-secondary" onClick={() => setRenameDevice(null)}>{t('userDashboard.renameCancel')}</button><button type="submit" className="btn btn-primary" disabled={renameSaving}>{renameSaving ? t('profile.saving') : t('userDashboard.renameSave')}</button></div>
        </form>
      </Modal>}
    </div>
  );
}
